import React from "react";
import usePageTitle from "../../hooks/usePageTitle";
import LegalLayout, { Section, P, UL, ContactCard } from "./LegalLayout";

const SECTIONS = [
  { id: "overview",     title: "Overview" },
  { id: "collection",   title: "Information We Collect" },
  { id: "usage",        title: "How We Use Your Information" },
  { id: "storage",      title: "Data Storage & Security" },
  { id: "sharing",      title: "Data Sharing" },
  { id: "retention",    title: "Data Retention" },
  { id: "rights",       title: "Your Rights" },
  { id: "cookies",      title: "Cookies" },
  { id: "children",     title: "Children's Privacy" },
  { id: "changes",      title: "Changes to This Policy" },
  { id: "contact",      title: "Contact Us" },
];

const BANNER = `${process.env.PUBLIC_URL}/illustrations/legal-privacy-banner.svg`;

const META = [
  { label: "Last updated",  value: "March 2026" },
  { label: "Jurisdiction",  value: "India" },
  { label: "Governing law", value: "Information Technology Act, 2000" },
];

const CONTACT = [
  { label: "Email",         value: "legal@easybilling.in" },
  { label: "Product",       value: "EasyBilling" },
  { label: "Country",       value: "India" },
  { label: "Response time", value: "Within 30 business days" },
];

export default function PrivacyPolicy() {
  usePageTitle("Privacy Policy");
  return (
    <LegalLayout
      current="privacy"
      eyebrow="Privacy Policy"
      title="How EasyBilling handles your data"
      lede="We believe in transparency. This policy explains exactly what data we collect, why we collect it, and how we protect it. Written in plain English — no legalese."
      meta={META}
      sections={SECTIONS}
      banner={BANNER}
    >
      <Section id="overview" title="Overview">
        <P>EasyBilling ("we", "us", "our") is a cloud-based invoicing and business management platform built for Indian businesses. This Privacy Policy explains how we collect, use, store, and protect information about you when you use our service at easybilling.in and any related applications.</P>
        <P>By creating an account and using EasyBilling, you agree to the practices described in this policy. If you do not agree, please do not use our service.</P>
        <P>This policy is governed by the <strong>Information Technology Act, 2000</strong> and the <strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</strong> of India.</P>
      </Section>

      <Section id="collection" title="Information We Collect">
        <P><strong>Account Information</strong> — when you register, we collect:</P>
        <UL items={[
          "Your full name and email address",
          "A hashed (encrypted) version of your password — we never store your password in plain text",
          "Business details provided during onboarding: company name, GSTIN, PAN, state, address, phone number, industry type, and business type",
        ]} />
        <P><strong>Business Data</strong> — data you create while using EasyBilling:</P>
        <UL items={[
          "Invoices, purchase orders, and credit notes you generate",
          "Client and supplier profiles including names, GSTINs, addresses, and contact details",
          "Product and service catalogues including HSN/SAC codes, prices, and stock levels",
          "Bank account details and UPI IDs you add for invoice payment display",
          "Financial reports and GST data derived from your transactions",
          "Salesperson profiles and commission records",
        ]} />
        <P><strong>Technical Data</strong> — automatically collected when you use the service:</P>
        <UL items={[
          "Browser type, operating system, and device information",
          "IP address and approximate geographic location",
          "Pages visited and features used within the application",
          "Date and time of access",
        ]} />
        <P>We do <strong>not</strong> collect payment card details. EasyBilling does not process payments — it only facilitates invoicing.</P>
      </Section>

      <Section id="usage" title="How We Use Your Information">
        <P>We use the information we collect solely to operate and improve EasyBilling. Specifically:</P>
        <UL items={[
          "To create and manage your account and authenticate your identity",
          "To populate your invoices, purchase orders, and reports with your business details",
          "To generate and display GST-compliant invoices and tax reports",
          "To maintain your client, supplier, and product databases",
          "To send transactional emails such as account verification or password reset (when implemented)",
          "To diagnose technical issues and improve application performance",
          "To comply with applicable Indian laws and tax regulations",
        ]} />
        <P>We do <strong>not</strong> use your data for advertising. We do <strong>not</strong> build user profiles for marketing purposes. We do <strong>not</strong> sell your data to third parties under any circumstances.</P>
      </Section>

      <Section id="storage" title="Data Storage & Security">
        <P>Your data is stored in a MongoDB database. Access to the database is restricted to authorised systems only and is not publicly accessible.</P>
        <P>We implement the following security measures:</P>
        <UL items={[
          "Passwords are hashed using bcrypt with a salt factor of 10 — your actual password is never stored",
          "All authenticated API requests require a valid JSON Web Token (JWT)",
          "HTTPS encryption is enforced for all data in transit",
          "Each user's data is logically isolated — one user cannot access another's data",
          "Sensitive fields such as bank account numbers are stored only as entered by you and are not processed or transmitted to any payment system",
        ]} />
        <P>While we take all reasonable precautions, no system can guarantee absolute security. In the event of a data breach that may affect your information, we will notify you by email within a reasonable timeframe as required by applicable law.</P>
      </Section>

      <Section id="sharing" title="Data Sharing">
        <P>EasyBilling does not sell, rent, or trade your personal information or business data to any third party.</P>
        <P>We may share information only in the following limited circumstances:</P>
        <UL items={[
          "With infrastructure providers (such as hosting and database services) who process data on our behalf under strict confidentiality obligations",
          "If required by law, court order, or lawful government authority under Indian law",
          "With your explicit consent for a specific purpose",
          "In the event of a merger or acquisition, in which case you will be notified before your data is transferred or becomes subject to a different privacy policy",
        ]} />
        <P>Public invoice links (shareable invoice URLs) are designed to be accessible by anyone with the link. Only include information in invoices that you are comfortable sharing with your clients.</P>
      </Section>

      <Section id="retention" title="Data Retention">
        <P>We retain your data for as long as your account is active. If you delete your account:</P>
        <UL items={[
          "Your personal account information is permanently deleted within 30 days",
          "Invoice and financial records may be retained for up to 7 years to comply with Indian tax laws (Section 44AA of the Income Tax Act and GST record-keeping requirements)",
          "Anonymised, aggregated data (with no personally identifiable information) may be retained indefinitely for statistical purposes",
        ]} />
        <P>You may request deletion of your data at any time by contacting us at <strong>legal@easybilling.in</strong>. We will process your request in accordance with applicable law.</P>
      </Section>

      <Section id="rights" title="Your Rights">
        <P>Under the Information Technology Act and applicable Indian privacy regulations, you have the right to:</P>
        <UL items={[
          "Access the personal data we hold about you by logging into your account or contacting us",
          "Correct inaccurate or outdated information through your account settings",
          "Request deletion of your account and associated personal data",
          "Withdraw consent for optional data processing at any time",
          "Receive a copy of your data in a portable format (CSV export of invoices and reports is available within the app)",
          "Lodge a complaint with the appropriate authority if you believe your data rights have been violated",
        ]} />
        <P>To exercise any of these rights, contact us at <strong>legal@easybilling.in</strong>. We will respond within 30 days.</P>
      </Section>

      <Section id="cookies" title="Cookies">
        <P>EasyBilling uses minimal browser storage to operate:</P>
        <UL items={[
          "A JWT authentication token is stored in your browser's localStorage to keep you logged in. This is essential for the application to function and is not used for tracking.",
          "We do not use third-party advertising cookies",
          "We do not use tracking pixels or cross-site tracking technologies",
        ]} />
        <P>You can clear your browser's localStorage at any time, which will log you out of EasyBilling. No other cookies are set by our application.</P>
      </Section>

      <Section id="children" title="Children's Privacy">
        <P>EasyBilling is a business tool intended for use by adults operating businesses in India. We do not knowingly collect personal information from anyone under the age of 18.</P>
        <P>If you believe a minor has created an account, please contact us at <strong>legal@easybilling.in</strong> and we will promptly delete the account and associated data.</P>
      </Section>

      <Section id="changes" title="Changes to This Policy">
        <P>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make material changes, we will:</P>
        <UL items={[
          "Update the 'Last updated' date at the top of this page",
          "Display a notice within the application for logged-in users",
          "Send an email notification for significant changes that affect your rights",
        ]} />
        <P>Continued use of EasyBilling after any changes constitutes your acceptance of the updated policy. We encourage you to review this page periodically.</P>
      </Section>

      <Section id="contact" title="Contact Us">
        <P>If you have any questions, concerns, or requests regarding this Privacy Policy or how your data is handled, please contact us:</P>
        <ContactCard rows={CONTACT} />
      </Section>
    </LegalLayout>
  );
}
